import { useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Bell, BellOff, AlertTriangle, Loader2, FileDown, Gavel } from 'lucide-react';
import { toast } from 'sonner';

const fmtD = (iso) => {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return d ? `${d}-${m}-${y}` : iso;
};

const STAGES = [
  { key: 'reminder1', type: 'invoice_herinnering_1', label: 'Stap 1 · Betalingsherinnering', short: 'Herinnering' },
  { key: 'reminder2', type: 'invoice_aanmaning', label: 'Stap 2 · Aanmaning', short: 'Aanmaning' },
  { key: 'reminder3', type: 'invoice_wik', label: 'Stap 3 · 14-dagenbrief (ingebrekestelling)', short: '14-dagenbrief' },
];

const FINAL_NOTICE = { key: 'reminder4', type: 'invoice_incasso_aankondiging', label: 'Laatste herinnering vóór incasso-overdracht', short: 'Laatste herinnering' };

const isOpen = (inv) => inv.type !== 'credit' && inv.status !== 'betaald' && inv.mollie_payment?.status !== 'paid';

export const ReminderBadge = ({ inv }) => {
  if (!isOpen(inv)) return null;
  if (inv.incasso_ready_at) {
    return <span className="text-xs bg-red-600 text-white px-2 py-0.5 rounded font-semibold flex items-center gap-1" data-testid={`reminder-badge-${inv.id}`}><AlertTriangle className="w-3 h-3" /> Klaar voor incasso</span>;
  }
  const stage = Number(inv.reminder_stage || 0);
  const last = stage ? inv.reminders?.[`reminder${stage}`]?.sent_at : null;
  if (inv.reminders_paused) {
    return <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded font-medium flex items-center gap-1" title="Automatische herinneringen gepauzeerd" data-testid={`reminder-badge-${inv.id}`}><BellOff className="w-3 h-3" /> Herinneringen uit{stage ? ` · ${STAGES[stage - 1].short} ${fmtD(last)}` : ''}</span>;
  }
  if (inv.reminders?.reminder4?.sent_at) {
    return <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded font-semibold flex items-center gap-1" title="Laatste herinnering vóór incasso verstuurd" data-testid={`reminder-badge-${inv.id}`}><Gavel className="w-3 h-3" /> Laatste herinnering {fmtD(inv.reminders.reminder4.sent_at)}</span>;
  }
  if (!stage) return null;
  const colors = ['bg-amber-100 text-amber-800', 'bg-orange-100 text-orange-800', 'bg-red-100 text-red-800'];
  return (
    <span className={`text-xs ${colors[stage - 1]} px-2 py-0.5 rounded font-semibold flex items-center gap-1`} title={`${STAGES[stage - 1].label} verstuurd op ${fmtD(last)}`} data-testid={`reminder-badge-${inv.id}`}>
      <Bell className="w-3 h-3" /> {STAGES[stage - 1].short} {fmtD(last)}
    </span>
  );
};

export const ReminderMenu = ({ inv, onUpdated }) => {
  const [busy, setBusy] = useState(false);
  if (!isOpen(inv) || !inv.client_email) return null;

  const send = async (stage) => {
    if (!window.confirm(`${stage.label} nu versturen naar ${inv.client_email}?`)) return;
    setBusy(true);
    try {
      await axiosInstance.post(`/invoices/${inv.id}/send-reminder?template_type=${stage.type}`);
      toast.success(`${stage.short} verstuurd naar ${inv.client_email}`);
      onUpdated && onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Versturen mislukt');
    } finally {
      setBusy(false);
    }
  };
  const downloadDossier = async (soort) => {
    setBusy(true);
    try {
      const r = await axiosInstance.get(`/invoices/${inv.id}/incasso-dossier?soort=${soort}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `Incassodossier_${inv.number}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Incassodossier gedownload');
    } catch (e) {
      toast.error('Dossier maken mislukt');
    } finally {
      setBusy(false);
    }
  };
  const togglePause = async () => {
    setBusy(true);
    try {
      await axiosInstance.patch(`/invoices/${inv.id}/reminders`, { paused: !inv.reminders_paused });
      toast.success(inv.reminders_paused ? 'Automatische herinneringen weer aan' : 'Automatische herinneringen uit voor deze factuur');
      onUpdated && onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Opslaan mislukt');
    } finally {
      setBusy(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" disabled={busy} className="text-orange-700 hover:bg-orange-50 border border-orange-200" title="Herinneringstraject" data-testid={`reminder-menu-${inv.id}`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4 mr-1" />}<span className="text-xs">Herinnering</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="text-xs text-gray-500">Herinneringstraject (NL) — nu versturen</DropdownMenuLabel>
        {STAGES.map((s, i) => {
          const sent = inv.reminders?.[s.key]?.sent_at;
          return (
            <DropdownMenuItem key={s.key} onClick={() => send(s)} data-testid={`reminder-send-${i + 1}-${inv.id}`} className="flex flex-col items-start gap-0.5">
              <span className="font-medium">{s.label}</span>
              <span className="text-[11px] text-gray-500">{sent ? `Verstuurd op ${fmtD(sent)} — opnieuw versturen` : i === 2 ? 'Wettelijke 14-dagentermijn + incassokosten' : 'Nog niet verstuurd'}</span>
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs text-gray-500">Incasso (handmatig)</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => send(FINAL_NOTICE)} data-testid={`reminder-send-final-${inv.id}`} className="flex flex-col items-start gap-0.5">
          <span className="font-medium flex items-center gap-1"><Gavel className="w-3.5 h-3.5" /> {FINAL_NOTICE.label}</span>
          <span className="text-[11px] text-gray-500">{inv.reminders?.reminder4?.sent_at ? `Verstuurd op ${fmtD(inv.reminders.reminder4.sent_at)} — opnieuw versturen` : '5 werkdagen, daarna overdracht — met totaal incl. kosten en rente'}</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => downloadDossier('consument')} data-testid={`dossier-consument-${inv.id}`}>
          <FileDown className="w-4 h-4 mr-2" /> Incassodossier downloaden (particulier, rente 4%)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => downloadDossier('zakelijk')} data-testid={`dossier-zakelijk-${inv.id}`}>
          <FileDown className="w-4 h-4 mr-2" /> Incassodossier downloaden (zakelijk, handelsrente 10,4%)
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={togglePause} data-testid={`reminder-pause-${inv.id}`}>
          {inv.reminders_paused ? <><Bell className="w-4 h-4 mr-2" /> Automatische herinneringen weer aanzetten</> : <><BellOff className="w-4 h-4 mr-2" /> Automatische herinneringen uit voor deze factuur</>}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const ReminderSettingsBar = () => {
  const [s, setS] = useState(null);
  const [preview, setPreview] = useState(null);
  const [showList, setShowList] = useState(false);
  const loadPreview = () => axiosInstance.get('/invoices-reminders/preview').then((r) => setPreview(r.data)).catch(() => {});
  useEffect(() => { axiosInstance.get('/settings/invoice-reminders').then((r) => setS(r.data)).catch(() => {}); loadPreview(); }, []);
  if (!s) return null;
  const save = async (patch) => {
    try {
      const r = await axiosInstance.put('/settings/invoice-reminders', patch);
      setS(r.data);
      loadPreview();
      toast.success(patch.enabled === undefined ? 'Termijnen opgeslagen' : r.data.enabled ? 'Automatische herinneringen staan aan' : 'Automatische herinneringen staan uit');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Opslaan mislukt');
    }
  };
  const planned = preview?.planned || [];
  return (
    <div className="bg-white border border-gray-200 rounded px-3 py-2 space-y-2" data-testid="reminder-settings-bar">
      <div className="flex items-center gap-3 flex-wrap text-xs text-gray-600">
        <Switch checked={!!s.enabled} onCheckedChange={(v) => save({ enabled: v })} data-testid="reminder-auto-toggle" />
        <span className="font-medium text-gray-800">Automatische herinneringen {s.enabled ? 'aan' : 'uit'}</span>
        <span className="text-gray-400">·</span>
        <span>Herinnering</span>
        <input type="number" min="1" max="60" defaultValue={s.first_after_days} onBlur={(e) => Number(e.target.value) !== s.first_after_days && save({ first_after_days: Number(e.target.value) })} className="w-12 border rounded px-1 py-0.5 text-center" data-testid="reminder-first-days" />
        <span>dagen na vervaldatum → aanmaning en 14-dagenbrief elke</span>
        <input type="number" min="1" max="60" defaultValue={s.between_days} onBlur={(e) => Number(e.target.value) !== s.between_days && save({ between_days: Number(e.target.value) })} className="w-12 border rounded px-1 py-0.5 text-center" data-testid="reminder-between-days" />
        <span>dagen daarna · stopt automatisch zodra betaald</span>
        {preview && (
          <button type="button" onClick={() => setShowList((v) => !v)} className={`ml-auto font-semibold underline ${planned.length ? 'text-orange-700' : 'text-gray-500'}`} data-testid="reminder-preview-toggle">
            Volgende run (08:00 UTC): {planned.length} {planned.length === 1 ? 'mail' : 'mails'}{s.enabled ? '' : ' (uit — wordt niet verstuurd)'}
          </button>
        )}
      </div>
      {showList && planned.length > 0 && (
        <ul className="text-xs text-gray-700 divide-y divide-gray-100" data-testid="reminder-preview-list">
          {planned.map((p) => (
            <li key={p.id} className="py-1 flex flex-wrap gap-x-3">
              <span className="font-semibold text-[#1a5276]">{p.number}</span>
              <span>{p.client_name}</span>
              <span className="text-gray-500">{p.client_email}</span>
              <span className={p.action === 'incasso' ? 'text-red-700 font-semibold' : 'text-orange-700 font-medium'}>{p.label}</span>
              <span>€ {Number(p.open_amount || 0).toFixed(2).replace('.', ',')} open{p.days_overdue !== undefined ? ` · ${p.days_overdue} dagen te laat` : ''}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
